import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { ApiError, ApiRequestOptions, HttpMethod, QueryParams } from './models';
import { mapDateTimesToLocal, mapDateTimesToUtc } from './models';

function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof HttpErrorResponse) {
    const inner = err.error;
    if (inner instanceof DOMException && inner.name === 'AbortError') return true;
    if (typeof inner === 'object' && inner && 'name' in inner && (inner as { name: string }).name === 'AbortError') {
      return true;
    }
  }
  return typeof err === 'object' && err !== null && 'name' in err && (err as { name: string }).name === 'AbortError';
}

/** HttpClient has no typed `signal`; unsubscribe aborts the in-flight XHR. */
function takeUntilAbort<T>(source: Observable<T>, signal?: AbortSignal): Observable<T> {
  if (!signal) return source;
  return new Observable<T>((subscriber) => {
    if (signal.aborted) {
      subscriber.error(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }
    const onAbort = () => {
      subscriber.error(new DOMException('The operation was aborted.', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort);
    const sub = source.subscribe(subscriber);
    return () => {
      signal.removeEventListener('abort', onAbort);
      sub.unsubscribe();
    };
  });
}

@Injectable({
  providedIn: 'root',
})
export class ApiClient {
  private baseUrl = environment.apiUrl.replace(/\/+$/, '');
  private defaultHeaders: () => Record<string, string | undefined> = () => ({});

  constructor(private readonly http: HttpClient) {}

  configure(partial: {
    baseUrl?: string;
    defaultHeaders?: () => Record<string, string | undefined>;
  }) {
    if (partial.baseUrl !== undefined) {
      this.baseUrl = partial.baseUrl.replace(/\/+$/, '');
    }
    if (partial.defaultHeaders) {
      this.defaultHeaders = partial.defaultHeaders;
    }
  }

  get<T>(path: string, options: ApiRequestOptions = {}) {
    return this.request<T>('GET', path, options);
  }
  post<T>(path: string, options: ApiRequestOptions = {}) {
    return this.request<T>('POST', path, options);
  }
  put<T>(path: string, options: ApiRequestOptions = {}) {
    return this.request<T>('PUT', path, options);
  }
  patch<T>(path: string, options: ApiRequestOptions = {}) {
    return this.request<T>('PATCH', path, options);
  }
  delete<T>(path: string, options: ApiRequestOptions = {}) {
    return this.request<T>('DELETE', path, options);
  }

  /** Binary response, such as the organization logo. */
  async getBlob(path: string, options: ApiRequestOptions = {}): Promise<Blob> {
    const url = this.buildUrl(path, options.query);
    let headers = new HttpHeaders();
    for (const [k, v] of Object.entries(this.defaultHeaders())) {
      if (v !== undefined && v !== '') headers = headers.set(k, v);
    }
    for (const [k, v] of Object.entries(options.headers ?? {})) {
      if (v !== undefined && v !== '') headers = headers.set(k, v);
    }
    const request$ = this.http
      .request('GET', url, {
        headers,
        observe: 'response',
        responseType: 'blob',
        withCredentials: options.credentials !== 'omit',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          if (!res.body) throw new Error('Empty response');
          return res.body;
        }),
        catchError((err: unknown) => {
          if (isAbortError(err) || options.signal?.aborted) {
            return throwError(() => new DOMException('The operation was aborted.', 'AbortError'));
          }
          return throwError(() => this.mapHttpError(err, 'GET', url));
        }),
      );
    return firstValueFrom(takeUntilAbort(request$, options.signal));
  }

  /**
   * Sends multipart/form-data (useful for file uploads).
   * Provide `form` as FormData. Do not set Content-Type manually.
   */
  multipart<T>(path: string, form: FormData, options: Omit<ApiRequestOptions, 'body' | 'json'> = {}) {
    return this.request<T>('POST', path, { ...options, body: form });
  }

  async request<T>(method: HttpMethod, path: string, options: ApiRequestOptions = {}): Promise<T> {
    const url = this.buildUrl(path, options.query);
    let headers = new HttpHeaders();

    for (const [k, v] of Object.entries(this.defaultHeaders())) {
      if (v !== undefined && v !== '') headers = headers.set(k, v);
    }
    for (const [k, v] of Object.entries(options.headers ?? {})) {
      if (v !== undefined && v !== '') headers = headers.set(k, v);
    }

    let body: unknown = undefined;
    if (options.body !== undefined) {
      body = options.body;
    } else if (options.json !== undefined) {
      body = mapDateTimesToUtc(options.json);
      if (!headers.has('content-type')) {
        headers = headers.set('Content-Type', 'application/json');
      }
    }

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    const withCredentials = options.credentials !== 'omit';

    const request$ = this.http
      .request<T>(method, url, {
        body: body as object | string | ArrayBuffer | Blob | FormData | null | undefined,
        headers,
        observe: 'response',
        responseType: 'json',
        withCredentials,
      })
      .pipe(
        map((res: HttpResponse<T>) => {
          if (res.status === 204) return undefined as T;
          return mapDateTimesToLocal(res.body) as T;
        }),
        catchError((err: unknown) => {
          if (isAbortError(err) || options.signal?.aborted) {
            return throwError(() => new DOMException('The operation was aborted.', 'AbortError'));
          }
          return throwError(() => this.mapHttpError(err, method, url));
        }),
      );

    return firstValueFrom(takeUntilAbort(request$, options.signal));
  }

  private buildUrl(path: string, query?: QueryParams) {
    const p = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${this.baseUrl}${p}`);
    this.appendQuery(url, query);
    return url.toString();
  }

  private appendQuery(url: URL, query?: QueryParams) {
    if (!query) return;
    for (const [key, raw] of Object.entries(query)) {
      if (raw === undefined || raw === null) continue;
      if (Array.isArray(raw)) {
        for (const v of raw) {
          if (v === undefined || v === null) continue;
          url.searchParams.append(key, String(v));
        }
      } else {
        url.searchParams.set(key, String(raw));
      }
    }
  }

  private mapHttpError(err: unknown, method: HttpMethod, url: string): ApiError {
    if (err instanceof HttpErrorResponse) {
      return this.toApiError({
        message: err.message || 'Request failed',
        url,
        method,
        status: err.status,
        data: err.error,
      });
    }
    return this.toApiError({
      message: 'Network error',
      url,
      method,
      cause: err,
    });
  }

  private toApiError(input: {
    message: string;
    url: string;
    method: HttpMethod;
    status?: number;
    data?: unknown;
    cause?: unknown;
  }): ApiError {
    return {
      name: 'ApiError',
      message: input.message,
      url: input.url,
      method: input.method,
      status: input.status,
      data: input.data,
      cause: input.cause,
    };
  }
}
